import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import { cartBuyerIdentityUpdateMutation, cartQuery } from "@repo/shopify/src/queries/cart-gql";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import PageTitle from "@components/atoms/PageTitle";
import Captcha from "@components/molecules/Captcha/Captcha";

import { useCheckoutContext, withCheckoutProvider } from "@organisms/Checkout/CheckoutContext";
import OrderSummary from "@organisms/Checkout/OrderSummary";
import Payment from "@organisms/Checkout/Payment";
import ShippingAddress from "@organisms/Checkout/ShippingAddress";

import useCart from "@hooks/reducerHooks/useCart";
import useLayout from "@hooks/reducerHooks/useLayout";
import useMeta from "@hooks/reducerHooks/useMeta";
import useUser from "@hooks/reducerHooks/useUser";

import { URLS } from "@const/urls";

import { NOTIFICATIONS } from "@utils/notifications";

/**
 * FIXME
 * setCancelModalOpen 제거 요망 (useEffect를 사용하지 않고 useLayout 사용)
 */
function Checkout() {
  const navigate = useNavigate();

  const { modal, openModal, closeModal, openLoginModal, showFooter } = useLayout();

  const { cart } = useCart();
  const { accessToken, customer, fetchUser } = useUser();
  const { setMetaTag } = useMeta();
  const { extractCart, isBlockedCustomer, setIsProgressing } = useCheckoutContext();
  const { t } = useTranslation();

  const [needCaptcha, setNeedCaptcha] = useState(false);
  // 할인 코드 입력
  const [discountLoading, setDiscountLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const paymentKey = searchParams.get("paymentKey");
  const errorMessage = searchParams.get("message");
  const brandPay = searchParams.get("paymentType");

  useEffect(() => {
    if (paymentKey || errorMessage || brandPay) {
      setIsProgressing(true);
    }
  }, [paymentKey, errorMessage, brandPay]);

  const [setBuyerIdentity] = useMutation(gql(cartBuyerIdentityUpdateMutation.query), {
    onCompleted: async () => {
      await refetch();
    },
    onError: (error) => {
      openModal({
        title: "Payment Error",
        description: NOTIFICATIONS.ERROR_PAYMENT_SET.message,
        confirmButton: {
          label: "Okay",
          onClick: () => {
            closeModal();
          },
        },
      });

      datadogLogs.logger.error("[SHOPIFY][CartBuyerIdentityUpdate] Failed to mutation", error);
      throw new Error(`[SHOPIFY][CartBuyerIdentityUpdate] Failed to mutation : ${JSON.stringify(error)}`);
    },
  });

  const [fetchCheckout, { refetch }] = useLazyQuery(gql(cartQuery.query), {
    variables: { id: cart.id },
    onCompleted: async (data) => {
      // cart에 buyerIdentity가 없을 때의 fallback logic
      if (!data?.cart?.buyerIdentity?.customer) {
        await setBuyerIdentity({ variables: { cartId: cart.id, buyerIdentity: { customerAccessToken: accessToken } } });
      }

      extractCart(data?.cart);
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][Cart] Failed to query", error);
      throw new Error(`[SHOPIFY][Cart] Failed to query : ${JSON.stringify(error)}`);
    },
  });

  useEffect(() => {
    return () => {
      closeModal();
    };
  }, []);

  useEffect(() => {
    if (cart && !sessionStorage.getItem("CAPTCHA")) {
      const isNeedCaptcha = cart.lines.edges.some(({ node }) => node.merchandise.product.tags.includes("Captcha"));
      setNeedCaptcha(isNeedCaptcha);
    }
  }, [cart]);

  useEffect(() => {
    if (!accessToken) {
      /** 비로그인 사용자 체크아웃 페이지 진입 시 login 페이지로 이동 */
      navigate(URLS.LOGIN);
      return;
    }

    if (needCaptcha) return;

    if (accessToken && customer.phone) {
      /** 로그인, 본인인증 완료 사용자가 체크아웃 페이지 진입 시 체크아웃 query */
      fetchCheckout();
    } else {
      /** 본인인증 미완료 사용자일 때 홈으로 이동 및 본인인증 모달 열기 */
      navigate(URLS.HOME, { replace: true });
      openLoginModal({
        redirectUrl: URLS.CHECKOUT,
        callback: () => {
          fetchCheckout();
        },
      });
    }
  }, [accessToken, needCaptcha]);

  useEffect(() => {
    setMetaTag({ title: `TUNE.KR - CHECKOUT` });
  }, []);

  useEffect(() => {
    if (needCaptcha) {
      openModal({
        title: "CAPTCHA",
        description: t("account.checkout.captcha"),
        closeButton: false,
        isCaptcha: true,
        children: <Captcha setNeedCaptcha={setNeedCaptcha} />,
        hasButton: true,
      });
    } else if (modal.title === "CAPTCHA") closeModal();
  }, [needCaptcha]);

  useEffect(() => {
    if (isBlockedCustomer) {
      openModal({
        title: "ALERT",
        description: t("account.checkout.ban"),
        closeButton: false,
        confirmButton: {
          label: "Okay",
          onClick: () => {
            navigate(URLS.HOME);
            closeModal();
          },
        },
      });
    } else if (modal.title === "ALERT") closeModal();
  }, [isBlockedCustomer]);

  const membership = useMemo(() => {
    const customerTags = customer.tags.map((tag) => tag.toLowerCase());

    if (customerTags.includes("membership_test")) {
      const membershipTag = customerTags.find((tag) => tag.startsWith("membership_") && tag !== "membership_test");
      return membershipTag ? membershipTag.replace("membership_", "") : "member";
    }

    return (
      customer.tags
        .find((item) => item.includes("MEMBERSHIP"))
        ?.replace("MEMBERSHIP_", "")
        .toUpperCase() || "MEMBER"
    );
  }, []);

  return (
    <>
      {!needCaptcha && !isBlockedCustomer && (
        <>
          <PageTitle title={"Complete\nYour Order"} />
          <section className="tune-column-4 mb-5 flex flex-col gap-10">
            <ShippingAddress />
            <section>
              <h3 className="mb-2.5 uppercase">Payment Method</h3>
              <p>{t("account.checkout.save_card")}</p>
              {/* Toss Payments 결제 위젯 */}
              <div className="pc:min-h-45 min-h-58" id="payment-method" />
              {/* Toss Payments 약관 동의 */}
            </section>
            <OrderSummary />
            <Payment refetch={refetch} membership={membership} discountLoading={discountLoading} />
          </section>
        </>
      )}
    </>
  );
}

export default withCheckoutProvider(Checkout);
